import React, { useState } from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    ToggleButtonGroup,
    ToggleButton,
    Link,
} from '@mui/material';

const CommunityGuidelines = () => {
    const [language, setLanguage] = useState('es');

    const handleLanguageChange = (event, newLanguage) => {
        if (newLanguage !== null) {
            setLanguage(newLanguage);
        }
    };

    const content = {
        es: {
            title: 'Normas de la Comunidad de VideoCallShop',
            lastUpdated: 'Última actualización: 22 de abril de 2026',

            s1Title: '1. Nuestra misión',
            s1Body:
                'Queremos que VideoCallShop sea un espacio seguro para comerciar por video.',

            s2Title: '2. Respeto',
            s2Body:
                'Está prohibido el acoso, el odio y cualquier forma de discriminación por raza, género, orientación, religión, nacionalidad o discapacidad. También quedan prohibidas las amenazas de cualquier tipo.',

            s3Title: '3. Contenido prohibido',
            s3Intro: 'No está permitido publicar, enviar ni transmitir:',
            s3Items: [
                'Contenido sexual explícito o desnudez.',
                'Violencia gráfica o automutilación.',
                'Drogas ilegales.',
                'Armas.',
            ],

            s4Title: '4. Autenticidad',
            s4Body:
                'Prohibimos el engaño, las estafas, la venta de productos falsificados y las promesas imposibles de cumplir.',

            s5Title: '5. Datos de terceros',
            s5Body:
                'No compartas datos personales de otras personas sin su consentimiento.',

            s6Title: '6. Spam y manipulación',
            s6Body:
                'Quedan prohibidos el spam, la publicidad engañosa y cualquier intento de manipular la experiencia de otros usuarios.',

            s7Title: '7. Cómo reportar',
            s7Body:
                'Para reportar un mensaje, abrí el menú ⋮ sobre el mensaje y elegí "Reportar". También podés escribirnos a soporte@videocallshop.com.',

            s8Title: '8. Consecuencias',
            s8Intro: 'Dependiendo de la gravedad de la infracción, podemos aplicar una o más de las siguientes acciones:',
            s8Items: [
                'Ocultar el mensaje.',
                'Bloquear al usuario.',
                'Suspender la cuenta.',
                'Reportar a las autoridades.',
            ],
        },
        en: {
            title: 'VideoCallShop Community Guidelines',
            lastUpdated: 'Last updated: April 22, 2026',

            s1Title: '1. Our mission',
            s1Body:
                'We want VideoCallShop to be a safe space for doing business by video.',

            s2Title: '2. Respect',
            s2Body:
                'Harassment, hate, and any form of discrimination based on race, gender, orientation, religion, nationality, or disability are prohibited. Threats of any kind are also prohibited.',

            s3Title: '3. Prohibited content',
            s3Intro: 'You may not post, send, or broadcast:',
            s3Items: [
                'Explicit sexual content or nudity.',
                'Graphic violence or self-harm.',
                'Illegal drugs.',
                'Weapons.',
            ],

            s4Title: '4. Authenticity',
            s4Body:
                'Deception, scams, sales of counterfeit goods, and impossible promises are prohibited.',

            s5Title: '5. Third-party data',
            s5Body:
                "Do not share other people's personal data without their consent.",

            s6Title: '6. Spam and manipulation',
            s6Body:
                "Spam, misleading advertising, and any attempt to manipulate other users' experience are prohibited.",

            s7Title: '7. How to report',
            s7Body:
                'To report a message, open the ⋮ menu on the message and choose "Report". You can also email soporte@videocallshop.com.',

            s8Title: '8. Consequences',
            s8Intro:
                'Depending on the severity of the violation, we may apply one or more of the following actions:',
            s8Items: [
                'Hide the message.',
                'Block the user.',
                'Suspend the account.',
                'Report to the authorities.',
            ],
        },
    };

    const t = content[language];

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 3,
                        flexWrap: 'wrap',
                        gap: 2,
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h1"
                        sx={{ fontWeight: 'bold', flex: 1, minWidth: '200px' }}
                    >
                        {t.title}
                    </Typography>
                    <ToggleButtonGroup
                        value={language}
                        exclusive
                        onChange={handleLanguageChange}
                        aria-label="language selection"
                        size="small"
                    >
                        <ToggleButton value="es" aria-label="español">
                            ES
                        </ToggleButton>
                        <ToggleButton value="en" aria-label="english">
                            EN
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                    {t.lastUpdated}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.s1Title}
                </Typography>
                <Typography variant="body1" paragraph>
                    {t.s1Body}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.s2Title}
                </Typography>
                <Typography variant="body1" paragraph>
                    {t.s2Body}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.s3Title}
                </Typography>
                <Typography variant="body1" paragraph>
                    {t.s3Intro}
                </Typography>
                <Typography variant="body1" component="ul" sx={{ ml: 2, mb: 2 }}>
                    {t.s3Items.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.s4Title}
                </Typography>
                <Typography variant="body1" paragraph>
                    {t.s4Body}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.s5Title}
                </Typography>
                <Typography variant="body1" paragraph>
                    {t.s5Body}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.s6Title}
                </Typography>
                <Typography variant="body1" paragraph>
                    {t.s6Body}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.s7Title}
                </Typography>
                <Typography variant="body1" paragraph>
                    {t.s7Body}
                </Typography>

                <Typography variant="h5" component="h2" gutterBottom sx={{ mt: 4, mb: 2, fontWeight: 'bold' }}>
                    {t.s8Title}
                </Typography>
                <Typography variant="body1" paragraph>
                    {t.s8Intro}
                </Typography>
                <Typography variant="body1" component="ul" sx={{ ml: 2, mb: 2 }}>
                    {t.s8Items.map((item, index) => (
                        <li key={index}>{item}</li>
                    ))}
                </Typography>

                <Box sx={{ mt: 4, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Link href="/privacy-policy">
                        {language === 'es' ? 'Política de Privacidad' : 'Privacy Policy'}
                    </Link>
                    <Link href="/terms">
                        {language === 'es' ? 'Términos y Condiciones' : 'Terms of Service'}
                    </Link>
                    <Link href="/support">
                        {language === 'es' ? 'Soporte' : 'Support'}
                    </Link>
                </Box>
            </Paper>
        </Container>
    );
};

export default CommunityGuidelines;
